/* stylelint-disable selector-class-pattern */
import styled, {
  createGlobalStyle,
} from 'styled-components';
import {
  Button,
  TextField,
} from '@material-ui/core';

import {
  BackgroundElement,
  TextElementStyled,
  ThankYouMessageProps,
} from '@/core/interfaces/common';

import mediaQueries from '@/utils/mediaQueries';
import { colors } from '@/utils/constants';
import { hexToRgb } from '@/utils/helpers';

import { Theme } from './themes';

const {
  desktop,
  tablet,
} = mediaQueries;

interface ButtonElementStyled extends TextElementStyled {
  buttoncolor: string;
}

export const GlobalStyle = createGlobalStyle<{ theme: Theme }>`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    background: ${({ theme: { backgroundColor } }) => backgroundColor};
  }
/* stylelint-disable */
  div, span, applet, object, iframe,
  h1, h2, h3, h4, h5, h6, p, blockquote, pre,
  a, abbr, acronym, address, big, cite, code,
  del, dfn, em, img, ins, kbd, q, s, samp,
  small, strike, strong, sub, sup, tt, var,
  b, u, i, center,
  dl, dt, dd, ol, ul, li,
  fieldset, form, label, legend,
  table, caption, tbody, tfoot, thead, tr, th, td,
  article, aside, canvas, details, embed,
  figure, figcaption, footer, header, hgroup,
  menu, nav, output, ruby, section, summary,
  time, mark, audio, video {
    margin: 0;
    padding: 0;
    border: 0;
    font-size: 100%;
    font: inherit;
    vertical-align: baseline;
  }
  /* HTML5 display-role reset for older browsers */
  article, aside, details, figcaption, figure,
  footer, header, hgroup, menu, nav, section {
    display: block;
  }
  ol, ul {
    list-style: none;
  }
  blockquote, q {
    quotes: none;
  }
  blockquote::before, blockquote::after,
  q::before, q::after {
    content: '';
    content: none;
  }
  table {
    border-collapse: collapse;
    border-spacing: 0;
  }
  /* stylelint-enable */
`;

export const CircularProgressWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: calc(100% - 200px);
`;

export const SectionTitle = styled.h2<TextElementStyled>`
  width: 100%;
  margin: 13px 0;
  color: ${props => (props.textcolor || colors.tableHeaderGray)};
  font-weight: bold;
  font-size: 16px;
  line-height: 30px;
`;

export const SendMessageButton = styled(Button)<ButtonElementStyled>`
  width: 357px;
  height: 60px;
  background-color: ${props => (props.buttoncolor || colors.gold)};
  color: ${props => (props.textcolor || colors.black)};

  &.Mui-disabled {
    color: ${props => (props.textcolor || colors.white)};
  }

  &:hover {
    background-color: ${props => (props.buttoncolor || colors.gold)};
  }
`;

export const StyledTextField = styled(TextField)`
  width: 100%;
  margin-top: 30px;
  padding-top: 5px;
`;

export const TargetCard = styled.div<BackgroundElement>`
  border-radius: 4px;
  padding: 8px 24px;
  background-color: ${props => hexToRgb(props.backgroundcolor || colors.white, '0.05')};
  font-size: 16px;
  line-height: 28px;

  strong {
    display: block;
    font-weight: 500;
    font-size: 18px;
    line-height: 32px;
  }

  span {
    color: rgb(24 24 24 / 80%);
  }

  @media ${desktop} {
    padding: 15px 24px;
  }
`;

interface InputWrapperProps extends BackgroundElement {
  error?: boolean;
  errorMessage?: string;
  isRequired?: boolean;
}

export const InputWrapperStyles = styled.div<InputWrapperProps>`
  position: relative;

  label {
    overflow-x: hidden;
    display: flex;
    flex-direction: column-reverse;
    font-size: 12px;
  }

  select {
    margin-top: 21px;
    appearance: none;
    background: transparent;
    background-image: url("data:image/svg+xml;utf8,<svg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'><path fill-rule='evenodd' clip-rule='evenodd' d='M10.5898 0.589798L5.99976 5.1698L1.40976 0.589798L-0.000244141 1.9998L5.99976 7.9998L11.9998 1.9998L10.5898 0.589798Z' fill='DarkGray'/></svg>");
    background-position: top 25px right 22px;
    background-repeat: no-repeat;
  }

  input,
  textarea {
    margin-top: 4px;
  }

  input,
  textarea,
  select {
    box-sizing: border-box;
    outline: 0;
    border: ${props => (props.error ? '1px solid #d53c2d' : '1px solid rgba(24, 24, 24, 0.1)')};
    border-radius: 4px;
    padding: 14px 16px;
    font-size: 16px;
    line-height: 28px;

    &:disabled {
      background: rgb(24 24 24 / 5%);
      color: rgb(24 24 24 / 70%);
    }

    &:focus {
      border: 1px solid ${props => (props.backgroundcolor)};
    }
  }

  span {
    transition: all 0.2s;
    transform-origin: top left;

    &::after {
      content: ${props => (props.isRequired ? '" *"' : '""')};
      color: #d53c2d;
    }
  }

  input[placeholder=" "]:not(:focus):placeholder-shown + span {
    opacity: 0.7;
    transform: translateY(41px) translateX(12px) scale(1.33);
    pointer-events: none;
  }

  &::after {
    content: ${props => `"${props.error ? props.errorMessage : ''}"`};
    position: absolute;
    bottom: -16px;
    left: 0;
    color: #d53c2d;
    font-weight: 700;
    font-size: 12px;
    line-height: 14px;
  }
`;

export const HeadlineDescription = styled.p`
  padding-top: 40px;
  padding-bottom: 30px;
  font-weight: 700;
  font-size: 24px;
  line-height: 56px;
  text-align: center;

  @media ${tablet} {
    padding-top: 48px;
  }

  @media ${desktop} {
    padding-top: 80px;
    padding-bottom: 40px;
    font-size: 48px;
  }
`;

export const ConstHeaderDescription = styled.h2`
  max-width: 896px;
  padding-top: 12px;
  font-size: 16px;
  text-align: center;
`;

export const Description = styled(ConstHeaderDescription)`
  @media ${desktop} {
    font-size: 24px;
  }
`;

export const ContactFields = styled.div`
  display: grid;
  gap: 0 16px;
  padding-bottom: 32px;

  @media ${tablet} {
    grid-template-columns: 1fr 1fr;
  }

  @media ${desktop} {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

export const ThankYouMessage = styled.div`
  margin-top: 16px;
  font-weight: 400;
  font-size: 16px;
  line-height: 28px;

  img {
    max-width: 100%;
  }
`;

export const EmailThankYouMessage = styled(ThankYouMessage)<ThankYouMessageProps>`
  margin-top: 24px;
  text-align: ${props => (props.centered ? 'center' : 'left')};

  @media ${desktop} {
    margin-top: 40px;
  }
`;

export const Footer = styled.footer<BackgroundElement>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0 32px;
  background-color: ${props => (props.backgroundcolor)};
  color: ${props => (props.textcolor)};
  text-align: center;

  @media ${desktop} {
    padding-top: 64px;
  }
`;

export const FooterLink = styled.a`
  color: inherit;
  font-weight: bold;
  font-size: 12px;
  line-height: 14px;
  text-decoration: none;
`;

export const FormWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

export const Form = styled.div`
  position: relative;
  z-index: 1;
  width: 1064px;
  margin: -200px 16px 40px;
  border-radius: 4px;
  padding: 24px;
  background-color: ${colors.white};

  @media ${tablet} {
    margin-right: 54px;
    margin-left: 54px;
  }

  @media ${desktop} {
    margin-right: auto;
    margin-left: auto;
    padding: 24px 108px;
  }
`;

export const FormInputWrapper = styled.div`
  padding-top: 18px;
`;

export const FormRadioInputWrapper = styled.div`
  padding-top: 8px;
`;

export const ErrorMessage = styled.p`
  color: ${colors.errorField};
  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
`;

export const CenteredErrorMessage = styled(ErrorMessage)`
  text-align: center;
`;

export const GeneralErrorMessage = styled(ErrorMessage)`
  margin-top: 18px;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  text-align: center;
`;

export interface HeaderElement extends BackgroundElement {
  isActionTaken: boolean;
}

export const Header = styled.header<HeaderElement>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: ${props => (props.isActionTaken ? '100vh' : 'unset')};
  padding: 50px 16px 250px;
  background-color: ${props => props.backgroundcolor};
  color: ${props => props.textcolor};

  @media ${tablet} {
    padding: 50px 54px 250px;
  }

  &::after {
    content: "";
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    display: ${props => (props.isActionTaken ? 'none' : 'block')};
    height: 100%;
    background: inherit;
    transform: skewY(10deg);
    transform-origin: bottom left;
  }
`;

export const LandingPageWrapper = styled.div<BackgroundElement>`
  width: 100%;
  background-color: ${props => hexToRgb(props.backgroundcolor || colors.white, '0.05')};
  font-family: Roboto, sans-serif;
`;

export const Logo = styled.img`
  max-height: 50px;
`;

export const MainImage = styled.img`
  width: 100%;
  object-fit: cover;
`;

export const SecondDescription = styled.p`
  margin-top: 8px;
  color: rgb(24 24 24 / 60%);
  font-size: 18px;
  line-height: 32px;
  text-align: center;
  white-space: pre-wrap;

  @media ${desktop} {
    margin-top: 24px;
  }
`;

export const SubmitSection = styled.section`
  margin: 40px 0 30px;
`;

export const SubmitButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

interface SendButtonElement extends BackgroundElement {
  display?: 'block' | 'inline-block';
  marginbottom?: string;
}

export const BaseSendButton = styled.input<SendButtonElement>`
  display: ${props => (props.display || 'block')};
  width: 295px;
  max-width: 100%;
  height: 56px;
  margin-bottom: ${props => (props.marginbottom || '6px')};
  border: 0;
  border-radius: 4px;
  background-color: ${props => (props.backgroundcolor)};
  color: ${props => (props.textcolor)};
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  appearance: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const SendButton = styled(BaseSendButton)<SendButtonElement>`
  margin: 0 auto 5px;

  @media ${desktop} {
    margin-top: 32px;
  }
`;

export const StepButton = styled(BaseSendButton)<SendButtonElement>`
  margin: 0 auto 5px;

  @media ${desktop} {
    margin-top: 32px;
  }
`;

export const ShareIconsWrapper = styled.div`
  padding: 11px 0 55px;
`;

export const TextAreaDescription = styled(SecondDescription)`
  margin-top: 32px;
  text-align: justify;

  @media ${tablet} {
    margin-top: 40px;
  }

  @media ${desktop} {
    margin-top: 88px;
  }
`;

export const TextDescription = styled.div`
  margin-top: 32px;
  color: rgb(24 24 24 / 100%);
  font-size: 18px;
  line-height: 32px;
  white-space: pre-wrap;

  @media ${tablet} {
    margin-top: 40px;
  }

  @media ${desktop} {
    margin-top: 88px;
  }

  ul {
    margin: 0;
    padding-left: 20px;
    list-style: disc;
    line-height: 1rem;
  }

  li {
    line-height: 32px;  // Same as for general text
  }
`;

export const Title = styled.h1`
  max-width: 896px;
  padding: 0 24px 12px;
  font-weight: 700;
  font-size: 32px;
  text-align: center;

  @media ${desktop} {
    font-size: 60px;
  }
`;

export const VideoWrapper = styled.div`
  position: relative;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 */

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

export const WantMoreWrapper = styled.p`
  font-size: 14px;
  line-height: 24px;
`;

export const WhyTakeActionSection = styled.section`
  width: 100%;
  padding-bottom: 64px;
  background-color: ${colors.white};
`;

export const WhyTakeActionWrapper = styled.div`
  max-width: 846px;
  margin: 0 16px;

  @media ${tablet} {
    margin: 0 auto;
    padding: 0 20px;
  }

  @media ${desktop} {
    padding: 0;
  }
`;

export const LogoLink = styled.a`
  color: inherit;
  text-decoration: none;

  &:hover,
  &:focus,
  &:active {
    color: inherit;
    text-decoration: none;
  }
`;

export const LogoWrapper = styled.div`
  margin-bottom: 56px;
`;

export const TalkingPointsList = styled.ul`
  position: relative;
  width: 100%;
  margin: 10px;
`;

export const TalkingPointItem = styled.li<BackgroundElement>`
  position: relative;
  padding-bottom: 12px;
  list-style: none;
  font-size: 16px;

  &::after {
    content: "";
    position: absolute;
    top: 9px;
    left: -18px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: ${props => props.backgroundcolor};
  }
`;

export const SubjectInputWrapper = styled(FormInputWrapper)`
  padding-bottom: 18px;
`;

export const FormHeading = styled.h3`
  font-weight: 500;
  font-size: 24px;
`;

export const TalkingPointsHeading = styled(FormHeading)`
  margin-top: 30px;
`;

export const OfficialWrapper = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;

  @media ${tablet} {
    margin-top: 32px;
  }

  @media ${desktop} {
    margin-top: 42px;
  }
`;

export const TargetsWrapper = styled.div`
  display: grid;
  margin-top: 16px;
  gap: 8px 16px;

  @media ${tablet} {
    grid-template-columns: 1fr 1fr;
  }

  @media ${desktop} {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;
